import { useState, useRef } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export default function ImageUpload({ value, onChange, onPathChange }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview]     = useState(value || null);
  const [error, setError]         = useState('');
  const fileInputRef   = useRef();
  const cameraInputRef = useRef();

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }

    setUploading(true);
    setError('');

    const localPreview = URL.createObjectURL(file);
    setPreview(localPreview);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);

      const res = await fetch(`${API_URL}/uploads/product-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');

      setPreview(data.url);
      onChange(data.url);
      onPathChange?.(data.path);
    } catch (err) {
      console.error('[ImageUpload] upload error:', err);
      setError('Image upload failed. Please try again.');
      setPreview(value || null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onChange(null);
    onPathChange?.(null);
    if (fileInputRef.current)   fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>
        Product Image
      </label>

      {preview ? (
        <div style={{ position: 'relative', width: 120 }}>
          <img
            src={preview}
            alt="Product"
            style={{
              width: 120, height: 120, objectFit: 'cover',
              borderRadius: 10, border: '2px solid #E2E8F0', display: 'block',
            }}
          />
          {uploading && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'rgba(27,58,107,0.7)', borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#D4A017', fontSize: 12, fontWeight: 600,
            }}>
              Uploading...
            </div>
          )}
          {!uploading && (
            <button
              type="button"
              onClick={handleRemove}
              style={{
                position: 'absolute', top: -8, right: -8,
                background: '#C0392B', color: '#fff', border: 'none',
                borderRadius: '50%', width: 24, height: 24,
                fontSize: 14, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700,
              }}
            >
              ×
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10 }}>
          <label style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            width: 100, height: 100, border: '2px dashed #D4A017',
            borderRadius: 10, cursor: 'pointer', background: '#FDF6E3', gap: 6,
          }}>
            <span style={{ fontSize: 24 }}>🖼</span>
            <span style={{ fontSize: 11, color: '#92620A', fontWeight: 600, textAlign: 'center' }}>Gallery</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => uploadFile(e.target.files[0])}
            />
          </label>

          <label style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            width: 100, height: 100, border: '2px dashed #1B3A6B',
            borderRadius: 10, cursor: 'pointer', background: '#EEF2FF', gap: 6,
          }}>
            <span style={{ fontSize: 24 }}>📷</span>
            <span style={{ fontSize: 11, color: '#1B3A6B', fontWeight: 600, textAlign: 'center' }}>Camera</span>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={(e) => uploadFile(e.target.files[0])}
            />
          </label>
        </div>
      )}

      {error && (
        <p style={{ color: '#C0392B', fontSize: 12, marginTop: 6 }}>{error}</p>
      )}
      <p style={{ color: '#94A3B8', fontSize: 11, marginTop: 6 }}>Max 5MB. JPG, PNG or WebP.</p>
    </div>
  );
}
